import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Ctx } from './ctx';
import { Icon, type IconName } from './icons';
import { euro, fmtDate } from './format';
import { build, CAT, loadCompanyData, norm, type Company, type Data } from './views/CompaniesPage';
import { searchMail, type FoundMail } from './mailSearch';

/**
 * Ricerca unica (anche con Ctrl+K): aziende, persone, preventivi, fatture, visite, email e pagine del programma.
 * Si scrive, si sceglie con le frecce e si apre con Invio.
 */

interface Hit {
  key: string;
  group: string;
  icon: IconName;
  title: string;
  sub?: string;
  right?: string;
  go: () => void;
}

let cache: { at: number; data: Data } | null = null;

const QUOTE_LABEL: Record<string, string> = { draft: 'bozza', sent: 'inviato', accepted: 'accettato', rejected: 'rifiutato', expired: 'scaduto' };

export function SearchPalette({ ctx, pages, onClose }: { ctx: Ctx; pages: { key: string; label: string; section: string }[]; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState<Data | null>(cache && Date.now() - cache.at < 60_000 ? cache.data : null);
  const [mail, setMail] = useState<FoundMail[] | null>(null);
  const [mailBusy, setMailBusy] = useState(false);
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const me = ctx.me.user;

  useEffect(() => {
    if (data) return;
    loadCompanyData(ctx.api)
      .then((d) => {
        cache = { at: Date.now(), data: d };
        setData(d);
      })
      .catch(() => setData({ deals: [], contacts: [], clients: [], quotes: [], visits: [], invoices: [], tickets: [] }));
  }, [ctx.api, data]);

  // Posta: si cerca solo dopo una breve pausa nella scrittura (la ricerca sul server della posta è più lenta)
  useEffect(() => {
    setMail(null);
    const t = q.trim();
    if (t.length < 3) return;
    setMailBusy(true);
    const h = setTimeout(() => {
      searchMail(ctx.api, t)
        .then((r) => setMail(r))
        .catch(() => setMail([]))
        .finally(() => setMailBusy(false));
    }, 450);
    return () => {
      clearTimeout(h);
      setMailBusy(false);
    };
  }, [q, ctx.api]);

  const companies = useMemo(() => (data ? build(data) : []), [data]);

  const hits = useMemo<Hit[]>(() => {
    const s = norm(q);
    const raw = q.trim().toLowerCase();
    if (!s) return [];
    const has = (...parts: (string | null | undefined)[]) => {
      const h = norm(parts.filter(Boolean).join(' '));
      return h.includes(s) || parts.some((p) => !!p && p.toLowerCase().includes(raw));
    };
    const openCompany = (c: Company) => () => {
      ctx.setPage('companies', { company: c.key, q: q.trim(), at: Date.now() });
    };
    const out: Hit[] = [];
    for (const c of companies.filter((c) => has(c.name, c.clientName, ...c.contacts.flatMap((p) => [p.vat, p.company]))).slice(0, 6))
      out.push({
        key: `a${c.key}`,
        group: 'Aziende',
        icon: 'building',
        title: c.name,
        sub: [CAT[c.cat].label, c.deal?.source].filter(Boolean).join(' · '),
        right: c.deal?.valueCents ? euro(c.deal.valueCents) : undefined,
        go: openCompany(c),
      });
    for (const p of (data?.contacts ?? []).filter((p) => has(p.name, p.email, p.phone, p.roleTitle) && p.name !== p.company).slice(0, 5)) {
      const c = companies.find((x) => x.contacts.some((y) => y.id === p.id));
      out.push({
        key: `p${p.id}`,
        group: 'Persone',
        icon: 'user',
        title: p.name,
        sub: [p.roleTitle, c?.name ?? p.company, p.phone, p.email].filter(Boolean).join(' · '),
        go: c ? () => ctx.setPage('companies', { company: c.key, person: p.id, q: q.trim(), at: Date.now() }) : () => ctx.setPage('contacts'),
      });
    }
    for (const x of (data?.quotes ?? []).filter((x) => has(x.number, x.subject, x.recipientName, x.clientName)).slice(0, 5))
      out.push({
        key: `q${x.id}`,
        group: 'Preventivi',
        icon: 'note',
        title: `${x.number} · ${x.recipientName}`,
        sub: `${x.subject} · ${QUOTE_LABEL[x.status] ?? x.status}`,
        right: euro(x.taxableCents),
        go: () => ctx.setPage('quotes', { open: x.id }),
      });
    for (const i of (data?.invoices ?? []).filter((i) => has(i.number, i.subject, i.recipientName, i.clientName)).slice(0, 5))
      out.push({
        key: `f${i.id}`,
        group: 'Fatture',
        icon: 'euro',
        title: `${i.number ?? 'Bozza'} · ${i.clientName ?? i.recipientName}`,
        sub: `${i.subject}${i.issueDate ? ` · ${fmtDate(i.issueDate)}` : ''}${i.paidAt ? ' · pagata' : ''}`,
        right: euro(i.totalCents),
        go: () => ctx.setPage('invoices', { open: i.id }),
      });
    for (const v of (data?.visits ?? []).filter((v) => has(v.title, v.clientName, v.contactName, v.dealName)).slice(0, 4))
      out.push({
        key: `v${v.id}`,
        group: 'Visite',
        icon: 'mic',
        title: v.title,
        sub: `${fmtDate(v.startsAt)}${v.dealName ? ` · ${v.dealName}` : ''}`,
        go: () => ctx.setPage('visits', { open: v.id }),
      });
    for (const m of (mail ?? []).slice(0, 6))
      out.push({
        key: `m${m.folder}${m.uid}`,
        group: 'Email',
        icon: m.folderKind === 'sent' ? 'send' : 'mail',
        title: m.subject || '(senza oggetto)',
        sub: m.folderKind === 'sent' ? `a ${m.to.map((t) => t.name || t.address).join(', ')}` : `da ${m.from.name || m.from.address}`,
        right: m.date ? fmtDate(m.date) : undefined,
        go: () => ctx.setPage('mail', { search: q.trim(), folder: m.folder, openUid: m.uid }),
      });
    for (const p of pages.filter((p) => has(p.label, p.section)).slice(0, 4))
      out.push({ key: `g${p.key}`, group: 'Vai a', icon: 'chevronRight', title: p.label, sub: p.section, go: () => ctx.setPage(p.key) });
    return out;
  }, [q, companies, data, mail, pages, ctx, me.id]);

  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('.sp-hit.on')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const choose = (h: Hit | undefined) => {
    if (!h) return;
    onClose();
    h.go();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((i) => Math.min(i + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(hits[sel]);
    }
  };

  let lastGroup = '';
  return (
    <div className="sp-back" onMouseDown={onClose}>
      <div className="sp-box" onMouseDown={(e) => e.stopPropagation()} onKeyDown={onKey}>
        <div className="sp-input">
          <Icon name="search" size={18} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un'azienda, una persona, un preventivo, una fattura, un'email…" />
          <kbd>Esc</kbd>
        </div>
        <div className="sp-list" ref={listRef}>
          {!q.trim() ? (
            <div className="sp-hint">
              Scrivi qualcosa, per esempio <b>Carves</b>, un numero di preventivo o una P.IVA. Si apre anche con <kbd>Ctrl</kbd> + <kbd>K</kbd>.
            </div>
          ) : !data ? (
            <div className="sp-hint">Caricamento…</div>
          ) : hits.length === 0 && !mailBusy ? (
            <div className="sp-hint">Nessun risultato per «{q.trim()}».</div>
          ) : (
            hits.map((h, i) => {
              const head = h.group !== lastGroup;
              lastGroup = h.group;
              return (
                <React.Fragment key={h.key}>
                  {head && <div className="sp-group">{h.group}</div>}
                  <button type="button" className={`sp-hit${i === sel ? ' on' : ''}`} onMouseEnter={() => setSel(i)} onClick={() => choose(h)}>
                    <span className="sp-ic">
                      <Icon name={h.icon} size={16} />
                    </span>
                    <span className="sp-text">
                      <b>{h.title}</b>
                      {h.sub && <span className="muted small">{h.sub}</span>}
                    </span>
                    {h.right && <span className="sp-right">{h.right}</span>}
                  </button>
                </React.Fragment>
              );
            })
          )}
          {mailBusy && q.trim().length >= 3 && <div className="sp-hint small">Cerco anche nella posta…</div>}
        </div>
      </div>
    </div>
  );
}
